import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  AlertTriangle,
  BadgeCheck,
  GraduationCap,
  Briefcase,
  Trophy,
  BookOpen,
  Star,
  Calendar,
  Clock,
  Send,
  Github,
  Linkedin,
  Globe,
  Instagram,
  Inbox,
} from 'lucide-react';
import { useGetInstructorPublicProfileQuery } from '../../api/apiSlice';
import CourseCard from '../../components/course/CourseCard';
import { AVATAR_FALLBACK, setFallback } from '../../lib/placeholders';

// InstructorProfileView — публичная страница инструктора.
//
// Дизайн: всё на одной странице, вертикальный скролл. Никаких табов —
// юзер хочет видеть и био, и опыт, и курсы сразу без переключений.
//
// Что починено по сравнению со старой версией:
//   • react-icons/fa → lucide (консистентность со всем проектом)
//   • цена курса через единый CourseCard (computePricing) — раньше была без скидки
//   • navigate(-1) вместо несуществующего /instructors
//   • полная i18n RU/KK
//   • пустые секции не рендерятся (раньше показывались с empty-плейсхолдерами)
//   • убран дубликат-сайдбар «Краткая информация», который повторял hero

const SOCIAL_PLATFORMS = {
  telegram: {
    icon: Send,
    label: 'Telegram',
    color: 'text-sky-500',
    href: (v) => (v?.startsWith('http') ? v : `https://t.me/${v.replace('@', '')}`),
  },
  github:   { icon: Github,    label: 'GitHub',    color: 'text-gray-800 dark:text-gray-200', href: (v) => v },
  linkedin: { icon: Linkedin,  label: 'LinkedIn',  color: 'text-blue-600',                    href: (v) => v },
  instagram:{ icon: Instagram, label: 'Instagram', color: 'text-pink-500',                    href: (v) => v },
  website:  { icon: Globe,     label: 'Website',   color: 'text-emerald-500',                 href: (v) => v },
};

// Локализованный текст «X лет опыта» с правильной плюрализацией для русского.
function experienceText(t, years) {
  if (!years || years <= 0) return t('instructorProfile.exp.none');
  const mod10 = years % 10;
  const mod100 = years % 100;
  let key = 'instructorProfile.exp.years_many';
  if (mod10 === 1 && mod100 !== 11) key = 'instructorProfile.exp.years_one';
  else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100))
    key = 'instructorProfile.exp.years_few';
  return t(key, { count: years });
}

const InstructorProfileView = () => {
  const { instructorId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data, isLoading, error } = useGetInstructorPublicProfileQuery(instructorId);

  const profile = data?.data?.profile;
  const courses = data?.data?.courses || [];

  const socials = useMemo(() => {
    if (!profile?.socialLinks) return [];
    return Object.entries(profile.socialLinks)
      .filter(([k, v]) => SOCIAL_PLATFORMS[k] && v)
      .map(([k, v]) => ({ key: k, link: v, ...SOCIAL_PLATFORMS[k] }));
  }, [profile?.socialLinks]);

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <SkeletonProfile />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-950/30">
            <AlertTriangle className="h-7 w-7 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('instructorProfile.notFound')}
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t('instructorProfile.notFoundDesc')}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900 pb-16">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition"
        >
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </button>

        {/* ───────── HERO — mesh gradient + glow avatar + premium stats ───────── */}
        <header className="relative rounded-3xl overflow-hidden bg-white dark:bg-gray-900 shadow-xl ring-1 ring-slate-200/60 dark:ring-gray-800">
          {/* Mesh gradient banner — фикс высоты, чтобы имя НЕ налезало */}
          <div className="relative h-36 sm:h-44 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-fuchsia-600 to-rose-500" />
            <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-pink-400/40 blur-3xl" />
            <div className="absolute top-8 -left-20 h-64 w-64 rounded-full bg-indigo-400/40 blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.2),transparent_55%)]" />
            <div
              className="absolute inset-0 opacity-25"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                backgroundSize: '28px 28px',
              }}
            />
          </div>

          <div className="px-5 sm:px-10 pb-6 sm:pb-8">
            {/* Avatar overlaps banner. На десктопе socials справа от имени;
                на мобильнике — отдельной полосой ниже, чтобы все иконки видны. */}
            <div className="-mt-14 sm:-mt-16 flex justify-start">
              <div className="relative shrink-0">
                <div className="absolute -inset-1.5 rounded-full bg-gradient-to-br from-violet-400 via-fuchsia-400 to-rose-400 blur-md opacity-75" />
                <img
                  src={profile.userId?.avatarUrl || AVATAR_FALLBACK}
                  alt={profile.publicName}
                  onError={setFallback(AVATAR_FALLBACK)}
                  className="relative h-28 w-28 sm:h-32 sm:w-32 rounded-full object-cover ring-4 ring-white dark:ring-gray-900 shadow-2xl bg-gray-200"
                />
                <span
                  className="absolute bottom-1 right-1 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 ring-4 ring-white dark:ring-gray-900 shadow-lg"
                  title={t('instructorProfile.verifiedTooltip')}
                >
                  <BadgeCheck className="h-4 w-4 text-white" />
                </span>
              </div>
            </div>

            {/* Имя + чипсы + socials (desktop справа) */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white break-words leading-tight">
                  {profile.publicName}
                </h1>
                {(profile.specialization || profile.experience > 0) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {profile.specialization && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-50 to-fuchsia-50 dark:from-violet-900/30 dark:to-fuchsia-900/30 ring-1 ring-violet-200/60 dark:ring-violet-700/30 text-violet-700 dark:text-violet-300 text-xs font-semibold px-3 py-1.5">
                        <GraduationCap className="w-3.5 h-3.5" />
                        {profile.specialization}
                      </span>
                    )}
                    {profile.experience > 0 && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 ring-1 ring-emerald-200/60 dark:ring-emerald-700/30 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-3 py-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        {experienceText(t, profile.experience)}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {socials.length > 0 && (
                <div className="flex flex-wrap gap-2 shrink-0">
                  {socials.slice(0, 5).map((s) => {
                    const Icon = s.icon;
                    return (
                      <a
                        key={s.key}
                        href={s.href(s.link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={s.label}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white dark:bg-gray-800 ring-1 ring-slate-200 dark:ring-gray-700 hover:ring-violet-300 dark:hover:ring-violet-500 hover:-translate-y-0.5 shadow-sm hover:shadow-md transition-all"
                      >
                        <Icon className={`w-4 h-4 ${s.color}`} />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Stats — gradient карточки с большой цифрой и подложкой-иконкой */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat icon={BookOpen}      label={t('instructorProfile.stat.courses')}      value={courses.length} color="violet" />
              <Stat icon={GraduationCap} label={t('instructorProfile.stat.experience')}   value={profile.experience || 0} color="blue" />
              <Stat icon={Briefcase}     label={t('instructorProfile.workExperience')}    value={profile.workExperience?.length || 0} color="emerald" />
              <Stat icon={Trophy}        label={t('instructorProfile.stat.achievements')} value={profile.achievements?.length || 0} color="amber" />
            </div>
          </div>
        </header>

        {/* ───────── О ПРЕПОДАВАТЕЛЕ ───────── */}
        <Card icon={Star} title={t('instructorProfile.tabAbout')} color="violet">
          {profile.bio?.trim() ? (
            <p className="text-sm sm:text-base leading-7 text-gray-700 dark:text-gray-300 whitespace-pre-line break-words [overflow-wrap:anywhere]">
              {profile.bio}
            </p>
          ) : (
            <EmptyMini text={t('instructorProfile.bioEmpty')} />
          )}
        </Card>

        {/* ───────── ОБРАЗОВАНИЕ ───────── */}
        {profile.education?.length > 0 && (
          <Card icon={GraduationCap} title={t('instructorProfile.education')} color="blue">
            <ul className="space-y-3">
              {profile.education.map((edu, idx) => (
                <li
                  key={idx}
                  className="relative rounded-xl bg-gradient-to-r from-sky-50/60 to-transparent dark:from-sky-900/15 dark:to-transparent ring-1 ring-sky-100 dark:ring-sky-900/30 p-4 hover:ring-sky-300 dark:hover:ring-sky-700 transition"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {edu.institution}
                      </p>
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                        {edu.degree}
                      </p>
                      {edu.field && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {edu.field}
                        </p>
                      )}
                    </div>
                    {edu.year && (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-2 py-0.5 text-[11px] text-gray-600 dark:text-gray-300 shrink-0">
                        <Calendar className="w-3 h-3" /> {edu.year}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* ───────── ОПЫТ РАБОТЫ ───────── */}
        {profile.workExperience?.length > 0 && (
          <Card icon={Briefcase} title={t('instructorProfile.workExperience')} color="emerald">
            <ul className="space-y-3">
              {profile.workExperience.map((exp, idx) => (
                <li
                  key={idx}
                  className="relative rounded-xl bg-gradient-to-r from-emerald-50/60 to-transparent dark:from-emerald-900/15 dark:to-transparent ring-1 ring-emerald-100 dark:ring-emerald-900/30 p-4 hover:ring-emerald-300 dark:hover:ring-emerald-700 transition"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {exp.position}
                      </p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                        {exp.company}
                      </p>
                      {exp.description && (
                        <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300 mt-2 break-words">
                          {exp.description}
                        </p>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-2 py-0.5 text-[11px] text-gray-600 dark:text-gray-300 shrink-0 whitespace-nowrap">
                      <Calendar className="w-3 h-3" />
                      {exp.startYear || '—'} —{' '}
                      {exp.current ? t('instructorProfile.presentTime') : exp.endYear || '—'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* ───────── ДОСТИЖЕНИЯ ───────── */}
        {profile.achievements?.length > 0 && (
          <Card icon={Trophy} title={t('instructorProfile.achievements')} color="amber">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {profile.achievements.map((ach, idx) => (
                <li
                  key={idx}
                  className="relative rounded-xl bg-gradient-to-br from-amber-50 via-orange-50/60 to-transparent dark:from-amber-900/20 dark:via-orange-900/15 dark:to-transparent ring-1 ring-amber-200 dark:ring-amber-800/40 p-4 hover:ring-amber-400 dark:hover:ring-amber-600 transition"
                >
                  <div className="flex items-start gap-3">
                    <Star className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                        {ach.title}
                      </p>
                      {ach.issuer && (
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                          {t('instructorProfile.issuedBy')}: {ach.issuer}
                        </p>
                      )}
                      {ach.description && (
                        <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300 mt-1.5 break-words">
                          {ach.description}
                        </p>
                      )}
                      {ach.year && (
                        <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3 h-3" /> {ach.year}{' '}
                          {t('instructorProfile.yearShort')}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* ───────── КУРСЫ ───────── */}
        <Card
          icon={BookOpen}
          title={t('instructorProfile.tabCourses')}
          color="rose"
          rightSlot={
            courses.length > 0 ? (
              <span className="inline-flex items-center rounded-full bg-gradient-to-r from-rose-500 to-pink-500 text-white px-3 py-1 text-xs font-bold tabular-nums shadow-sm">
                {courses.length}
              </span>
            ) : null
          }
        >
          {courses.length > 0 ? (
            // Тот же CourseCard, что и публичный каталог — цена/скидка
            // через computePricing, гарантия 1-в-1 отображения.
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.map((c) => (
                <CourseCard key={c._id} course={c} />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-10 text-center">
              <Inbox className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('instructorProfile.coursesEmpty')}
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// ─── building blocks ────────────────────────────────────────────────

// Палитра для Stat — gradient-карточки с подложкой-иконкой большого размера.
// Tailwind JIT не поддерживает динамические классы, поэтому хардкодим вариации.
const STAT_PALETTE = {
  violet:  { bg: 'from-violet-50 to-fuchsia-50 dark:from-violet-900/20 dark:to-fuchsia-900/20',  ring: 'ring-violet-200/60 dark:ring-violet-700/30',   icon: 'text-violet-600 dark:text-violet-400'   },
  blue:    { bg: 'from-sky-50 to-indigo-50 dark:from-sky-900/20 dark:to-indigo-900/20',          ring: 'ring-sky-200/60 dark:ring-sky-700/30',         icon: 'text-sky-600 dark:text-sky-400'         },
  emerald: { bg: 'from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20',     ring: 'ring-emerald-200/60 dark:ring-emerald-700/30', icon: 'text-emerald-600 dark:text-emerald-400' },
  amber:   { bg: 'from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20',     ring: 'ring-amber-200/60 dark:ring-amber-700/30',     icon: 'text-amber-600 dark:text-amber-400'     },
};

const Stat = ({ icon: Icon, label, value, color = 'violet' }) => {
  const c = STAT_PALETTE[color] || STAT_PALETTE.violet;
  return (
    <div className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${c.bg} ring-1 ${c.ring} p-4 sm:p-5 transition-all hover:shadow-md hover:-translate-y-0.5`}>
      {/* Большая подложка-иконка справа сверху — premium-приём */}
      <Icon className={`absolute -right-3 -top-3 w-20 h-20 opacity-10 group-hover:opacity-20 transition-opacity ${c.icon}`} />
      <div className="relative">
        <Icon className={`w-5 h-5 mb-2 ${c.icon}`} />
        <div className="text-2xl sm:text-3xl font-extrabold tabular-nums leading-none text-gray-900 dark:text-white">
          {value}
        </div>
        <div className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400 mt-1.5 font-semibold truncate">
          {label}
        </div>
      </div>
    </div>
  );
};

// Card icon palette — gradient-иконка вместо плоского fill.
const CARD_PALETTE = {
  violet:  'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/20',
  blue:    'bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-lg shadow-sky-500/20',
  emerald: 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/20',
  amber:   'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/20',
  rose:    'bg-gradient-to-br from-rose-500 to-pink-500 text-white shadow-lg shadow-rose-500/20',
  // legacy aliases на случай неполного миграции
  indigo:  'bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/20',
  purple:  'bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white shadow-lg shadow-purple-500/20',
};

const Card = ({ icon: Icon, title, color = 'violet', rightSlot, children }) => (
  <section className="rounded-2xl bg-white dark:bg-gray-900 ring-1 ring-slate-200/60 dark:ring-gray-800 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between gap-3 px-5 py-4 sm:px-6 border-b border-slate-100 dark:border-gray-800">
      <h2 className="flex items-center gap-3 text-base sm:text-lg font-bold text-gray-900 dark:text-white">
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${CARD_PALETTE[color]}`}>
          <Icon className="w-4 h-4" />
        </span>
        {title}
      </h2>
      {rightSlot}
    </div>
    <div className="p-5 sm:p-6">{children}</div>
  </section>
);

const EmptyMini = ({ text }) => (
  <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
    {text}
  </div>
);

const SkeletonProfile = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-32 rounded-3xl bg-gray-200 dark:bg-gray-800" />
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
      <div className="flex gap-4 -mt-12">
        <div className="h-24 w-24 rounded-2xl bg-gray-200 dark:bg-gray-800" />
        <div className="flex-1 space-y-2 pt-12">
          <div className="h-5 w-2/3 rounded bg-gray-200 dark:bg-gray-800" />
          <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-800" />
        </div>
      </div>
      <div className="mt-5 grid grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-16 rounded-xl bg-gray-200 dark:bg-gray-800" />
        ))}
      </div>
    </div>
    <div className="h-48 rounded-2xl bg-gray-200 dark:bg-gray-800" />
  </div>
);

export default InstructorProfileView;
